import React,{useContext, useEffect, useRef} from "react";  
import { winMemes, drawMemes } from "./memeLists";
import GameRoomContext from "../../context/GameRoomContext";
import SocketContext from "../../context/SocketContext";
import UserContext from "../../context/UserContext";
import useGameBase from '../../hooks/useGameBase';
import computeReadyInfo from './readyHelpers';
import ReadyControl from './ReadyControl';
import PlayerReadyList from './PlayerReadyList';


function GameRoomPlayAgain() {
  const rootRef = useRef(null);

  const {gameRoomData, onTogglePlayerReady, onPlayAgain, onRoomParticipantAction} = useContext(GameRoomContext);
  // Remember previous config for play again
  const previousConfig = gameRoomData?.gameRoomConfig || {};
  const { user } = useContext(UserContext);
  const socket = useContext(SocketContext);
  const result = gameRoomData?.gameRoomGame?.result || null;
  const resultOutcome = result?.outcome || null;
  const gameState = gameRoomData?.gameRoomGame?.gameState || {};
  const gamePlayers = gameRoomData?.gameRoomPlayers || [];
  const roomId = gameRoomData?.gameRoomDetails?.roomId || '';
  const { findPlayerById } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const readyInfo = computeReadyInfo(gameRoomData, socket, user);
  const { nonHostPlayers, readyCount, allReady, canHostStartSolo, isHost, isSpectator, readyPlayers, isReady } = readyInfo;
  const nextRoundPendingPlayers = gameRoomData?.gameRoomDetails?.nextRoundPendingPlayers || [];
  const nextRoundQueuedCount = nextRoundPendingPlayers.length;
  const nextRoundSlotsRemaining = gameRoomData?.gameRoomDetails?.nextRoundSlotsRemaining ?? 0;
  const spectators = gameRoomData?.gameRoomSpectators || [];
  const highScoreAnnouncements = Array.isArray(gameState?.highScoreAnnouncements) ? gameState.highScoreAnnouncements : [];

  useEffect(() => {
    if (!resultOutcome) return undefined;

    const scrollAllToTop = () => {
      const root = rootRef.current;
      if (!root) return;

      let node = root.parentElement;
      while (node) {
        if (node.scrollHeight > node.clientHeight) {
          node.scrollTop = 0;
        }
        node = node.parentElement;
      }

      const scrollingElement = document.scrollingElement || document.documentElement || document.body;
      if (scrollingElement) {
        scrollingElement.scrollTop = 0;
      }
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      root.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
    };

    scrollAllToTop();
    const raf = requestAnimationFrame(scrollAllToTop);
    return () => cancelAnimationFrame(raf);
  }, [resultOutcome, gameRoomData?.gameRoomGame?.gameState?.finishedAt]);

  //console.log("isHost - " + isHost + " isSpectator - " + isSpectator + " socket.id - " + socket.id + " hostUserId - " + hostUserId);
  if (!resultOutcome) return null;
  
  const winnerNames = result.winners
    .map((winnerId) => {
      const p = findPlayerById(winnerId) || (gameRoomData?.gameRoomPlayers || []).find((pl) => pl && (String(pl.socketId) === String(winnerId) || String(pl.userId) === String(winnerId) || String(pl.id) === String(winnerId)));
      return p ? p.username : winnerId;
    })
    .filter(Boolean);

  // Meme logic
  // winMemes and drawMemes are imported from memeLists.js (auto-generated)

  // Small stable string hash -> number
  function stableHash(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
  }

  // Helper to pick a meme deterministically for all clients when a seed is provided
  function pickMeme(memes, seed) {
    if (!memes || memes.length === 0) return null;
    if (!seed) {
      // fallback to random if no seed
      return memes[Math.floor(Math.random() * memes.length)];
    }
    const hash = stableHash(String(seed));
    const idx = hash % memes.length;
    return memes[idx];
  }

  let memeUrl = null;
  if ((result.outcome === "win" || result.outcome === "tie") && winMemes.length > 0) {
    // Use a shared seed so all clients pick the same meme (roomId + winners)
  const roomId = gameRoomData?.gameRoomDetails?.roomId || '';
  const finishedAt = gameRoomData?.gameRoomGame?.gameState?.finishedAt || '';
  const round = gameRoomData?.gameRoomGame?.gameState?.round || '';
  // include round counter in seed as an additional entropy source
  const seed = roomId + ':' + finishedAt + ':' + round + ':' + (Array.isArray(result.winners) ? result.winners.join(',') : String(result.winners || ''));
    const memeFile = pickMeme(winMemes, seed);
    memeUrl = memeFile ? `/memes/win/${memeFile}` : null;
  } else if (result.outcome === "draw" && drawMemes.length > 0) {
  const roomId = gameRoomData?.gameRoomDetails?.roomId || '';
  const finishedAt = gameRoomData?.gameRoomGame?.gameState?.finishedAt || '';
  const round = gameRoomData?.gameRoomGame?.gameState?.round || '';
  const seed = roomId + ':' + finishedAt + ':' + round + ':draw';
    const memeFile = pickMeme(drawMemes, seed);
    memeUrl = memeFile ? `/memes/draw/${memeFile}` : null;
  }

  const hostVisibleSpectators = spectators.filter(Boolean);

  return (
    <div ref={rootRef} className="mt-6 flex flex-col gap-2 mb-4">
      <div className="bg-gray-100 dark:bg-gray-800 rounded p-4 mt-4 text-center">
        {result.outcome === "win" && winnerNames.length > 0 && (
          <div className="text-green-700 dark:text-green-300 text-lg font-semibold">
            Winner{winnerNames.length > 1 ? "s" : ""}: {winnerNames.join(", ")}
          </div>
        )}
        {result.outcome === "draw" && (
          <div className="text-yellow-700 dark:text-yellow-300 text-lg font-semibold">Draw!</div>
        )}
        {result.outcome === "loss" && (
          <div className="text-red-700 dark:text-red-300 text-lg font-semibold">Loss</div>
        )}
        {/* Show message if available */}
        {result.message && (
          <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
            {result.message}
          </div>
        )}
        {/* Show meme if available */}
        {memeUrl && (
          <div className="mt-4 flex justify-center">
            <img src={memeUrl} alt="Meme" style={{ maxWidth: "320px", maxHeight: "320px", borderRadius: "12px" }} />
          </div>
        )}
      </div> 

      {highScoreAnnouncements.length > 0 && (
        <div className="rounded bg-emerald-50 p-4 text-center text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
          {highScoreAnnouncements.map((entry) => (
            <div key={`${entry.playerId}-${entry.leaderboardKey}-${entry.rank}`}>
              {entry.username} reached #{entry.rank} in the top {entry.topLimit} for {entry.metricLabel} with {entry.scoreDisplay}.
            </div>
          ))}
        </div>
      )}

      <ReadyControl
        isHost={isHost}
        isSpectator={isSpectator}
        isReady={isReady}
        readyPlayers={readyPlayers}
        nonHostPlayers={nonHostPlayers}
        readyCount={readyCount}
        allReady={allReady}
        hostCanStartSolo={canHostStartSolo}
        onTogglePlayerReady={onTogglePlayerReady}
        onHostAction={() => onPlayAgain(previousConfig)}
        hostActionLabel={!allReady ? 'Waiting on players' : 'Play Again'}
        hostActionDisabled={!allReady}
        hostActionTitle={!allReady ? 'Waiting on players' : 'Play Again'}
      />
      {nextRoundQueuedCount > 0 && (
        <div className="text-sm text-gray-700 dark:text-gray-200 text-center">
          {nextRoundQueuedCount} spectator{nextRoundQueuedCount === 1 ? '' : 's'} queued for the next round.
        </div>
      )}

      {isHost && hostVisibleSpectators.length > 0 && (
        <div className="rounded bg-gray-100 p-4 dark:bg-gray-800">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Spectators for next round</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                Choose which spectators should be added as players when the next game starts.
              </p>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-300">
              {nextRoundSlotsRemaining} slot{nextRoundSlotsRemaining === 1 ? '' : 's'} left
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2">
            {hostVisibleSpectators.map((spectator) => {
              const participantId = spectator.userId || spectator.socketId || spectator.id;
              const queued = !!(participantId && nextRoundPendingPlayers.includes(participantId));
              const canQueue = queued || nextRoundSlotsRemaining > 0;

              return (
                <div
                  key={participantId || spectator.username}
                  className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {spectator.username}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {queued ? 'Queued for next round' : 'Spectating this round'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`rounded px-3 py-2 text-xs font-medium text-white transition ${queued ? 'bg-gray-400 dark:bg-gray-700 cursor-default' : canQueue ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600' : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'}`}
                    disabled={queued || !canQueue}
                    onClick={() => {
                      if (participantId && !queued && canQueue) {
                        onRoomParticipantAction && onRoomParticipantAction(roomId, participantId, 'makePlayer');
                      }
                    }}
                    title={queued ? 'Already queued for the next round' : canQueue ? 'Add as a player for the next round' : 'No seats left for the next round'}
                  >
                    {queued ? 'Queued' : 'Add to next game'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <PlayerReadyList nonHostPlayers={nonHostPlayers} readyPlayers={readyPlayers} />
    </div>
  );
}

export default GameRoomPlayAgain;
